/**
 * Smart Records — Premium feature section for landing pages
 * Shared between VerticalLanding (used by /estetica, /hair, /dentisti),
 * DentistaLanding (/dentista), BenesserePosturaleLanding (/benessere-posturale).
 *
 * Pass `sector` prop with realistic mock data.
 */
import React from "react";
import { Link } from "react-router-dom";
import { Crown, Check, Sparkles, ClipboardList, Shield } from "lucide-react";

const DEFAULTS = {
  accent: "#7C3AED",
  demoPath: "/demo",
  badge: "Cliente premium",
  clientName: "Cliente Demo",
  highlights: ["Anamnesi e cure", "Trattamenti e storico", "Documenti", "Preventivi e pacchetti", "Timeline completa", "AI summary"],
  stats: [
    { label: "Speso",  value: "€820" },
    { label: "Visite", value: "12" },
    { label: "Ultima", value: "3 gg" },
  ],
  timeline: ["Trattamento completato", "Documento clinico caricato", "AI Summary aggiornato"],
  aiHint: "Cliente attivo, frequenza in linea. Programmare prossimo follow-up.",
};

const SmartRecordsLandingSection = ({
  accent = DEFAULTS.accent,
  demoPath = DEFAULTS.demoPath,
  badge = DEFAULTS.badge,
  clientName = DEFAULTS.clientName,
  highlights = DEFAULTS.highlights,
  stats = DEFAULTS.stats,
  timeline = DEFAULTS.timeline,
  aiHint = DEFAULTS.aiHint,
  sectorTagline,
  testid = "landing-smart-records-section",
}) => {
  return (
    <section
      id="smart-records"
      className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-violet-50 via-white to-cyan-50"
      data-testid={testid}
    >
      <div className="max-w-6xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          {/* TEXT COLUMN */}
          <div>
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gradient-to-r from-violet-600 to-cyan-500 text-white text-[10px] font-bold uppercase tracking-[0.18em] mb-4 shadow-md">
              <Crown className="w-3 h-3" /> Smart Records · Premium
            </span>
            <h2 className="text-3xl md:text-4xl font-black text-slate-900 leading-tight">
              Ogni cliente, una scheda intelligente completa.
            </h2>
            <p className="text-slate-600 mt-4 text-lg leading-relaxed">
              {sectorTagline || "SmartBooker non è solo agenda. È il sistema operativo del tuo business."}
              {" "}
              {highlights.slice(0, 5).join(" · ")}.
            </p>
            <ul className="mt-6 space-y-3" data-testid="landing-sr-highlights">
              {highlights.slice(0, 6).map((h) => (
                <li key={h} className="flex items-start gap-2.5">
                  <span className="w-5 h-5 rounded-full bg-gradient-to-br from-violet-500 to-cyan-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-white" />
                  </span>
                  <span className="text-sm text-slate-700">{h}</span>
                </li>
              ))}
            </ul>
            <div className="mt-7 flex flex-wrap gap-3 items-center">
              <Link to={demoPath}>
                <button
                  className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 bg-gradient-to-r from-violet-600 to-cyan-500 hover:from-violet-700 hover:to-cyan-600 text-white shadow-lg text-sm font-semibold transition-all"
                  data-testid="landing-sr-cta"
                >
                  <Sparkles className="w-4 h-4" />
                  Vedi le Smart Records in azione
                </button>
              </Link>
              <span className="inline-flex items-center gap-1.5 text-xs text-slate-500">
                <Shield className="w-3 h-3" /> GDPR-ready · Audit log incluso
              </span>
            </div>
          </div>

          {/* MOCKUP CARD */}
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-br from-violet-400/20 to-cyan-400/20 blur-3xl rounded-full" />
            <div className="relative rounded-2xl border border-slate-200 bg-white shadow-2xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="w-2 h-2 rounded-full bg-red-300" />
                <span className="w-2 h-2 rounded-full bg-amber-300" />
                <span className="w-2 h-2 rounded-full bg-emerald-300" />
              </div>
              <div className="flex items-center gap-3 pb-3 border-b border-slate-100">
                <div
                  className="w-12 h-12 rounded-2xl text-white flex items-center justify-center font-black text-sm shadow"
                  style={{ background: `linear-gradient(135deg, ${accent}, ${accent}cc)` }}
                >
                  {clientName.split(" ").map(p => p[0]).join("").slice(0, 2)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-slate-900 truncate">{clientName}</p>
                  <p className="text-[10px] text-slate-500">{badge}</p>
                </div>
                <span className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 inline-flex items-center gap-1">
                  <Crown className="w-2.5 h-2.5" /> Premium
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-3">
                {stats.map(s => (
                  <div key={s.label} className="rounded-xl bg-slate-50 p-2 text-center">
                    <p className="text-[9px] uppercase tracking-wider text-slate-500 font-bold">{s.label}</p>
                    <p className="text-sm font-black text-slate-900">{s.value}</p>
                  </div>
                ))}
              </div>
              <ul className="mt-3 space-y-1.5">
                {timeline.slice(0, 3).map((t, i) => (
                  <li key={i} className="flex items-center gap-2 text-[11px]">
                    <span
                      className="w-5 h-5 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${accent}20` }}
                    >
                      <ClipboardList className="w-2.5 h-2.5" style={{ color: accent }} />
                    </span>
                    <span className="text-slate-700 truncate">{t}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-3 rounded-xl bg-gradient-to-r from-violet-50 to-cyan-50 border border-violet-200/60 p-2.5 flex items-start gap-2">
                <Sparkles className="w-3 h-3 text-violet-500 flex-shrink-0 mt-0.5" />
                <p className="text-[10px] text-slate-700">
                  <span className="font-bold text-violet-700">AI · </span>
                  {aiHint}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SmartRecordsLandingSection;
